/*
  What a file is about to do, worked out before it does it.

  Written 6 September 2026 for "OnHand's own templates, and an import that says
  where it is going".

  **Every import in OnHand was a one-way trip you could not see the end of.**
  You chose a file, pressed a button, and rows went in. Afterwards a message
  said "Imported 320 warehouses", which counted the rows it had written and made
  no distinction between a new location and one that already existed and was
  changed. Two of the seven counted a row it had failed to write as a success.

  **And none of them ever said which company they were loading into.** A row's
  organization comes from whichever one you are signed into, which is the right
  behaviour and has no confirmation anywhere. Once the real business is loaded,
  one import while pointed at the testing site puts tens of thousands of rows in
  the wrong place, silently.

  This file is the arithmetic; `components/ImportPreview.jsx` is the screen that
  shows it. All of it is here rather than in the component because this project
  has no way to render a component in a test, and the part that can be quietly
  wrong is the counting.
*/

/*
  `read` turns one row of the file into either something to write or a reason it
  cannot be read. It returns `{ key, payload }` or `{ error }`, and **a row it
  wants to ignore entirely returns nothing at all**, which is not the same as an
  error: a trailing blank line in a spreadsheet is not a fault worth reporting.
*/
export function planRows({ rows, read, existing }) {
  const have = existing instanceof Map ? existing : new Map(Object.entries(existing || {}));
  const create = [];
  const update = [];
  const unchanged = [];
  const unreadable = [];
  const seen = new Set();

  (rows || []).forEach((row, i) => {
    let out;
    try { out = read(row, i); }
    catch (e) { unreadable.push({ line: i + 2, why: e.message }); return; }
    if (!out) return;
    if (out.error) { unreadable.push({ line: i + 2, why: out.error }); return; }

    /*
      **The same key twice in one file is a fault worth naming.** A spreadsheet
      with a location listed twice would otherwise write it, then write it
      again, and report two rows changed when there is one.
    */
    if (seen.has(out.key)) {
      unreadable.push({ line: i + 2, why: 'the same entry appears earlier in this file' });
      return;
    }
    seen.add(out.key);

    const before = have.get(out.key);
    if (!before) create.push({ ...out, line: i + 2 });
    else if (changedFields(before, out.payload).length) {
      update.push({ ...out, line: i + 2, before, changed: changedFields(before, out.payload) });
    } else {
      unchanged.push({ ...out, line: i + 2, before });
    }
  });

  return { create, update, unchanged, unreadable };
}

/*
  Which fields this row would actually change.

  **Only the fields the file carries are compared**, so a template with four
  columns never counts the other fifty as "changed", and a row that says exactly
  what the database already says is reported as unchanged rather than as work.

  `updated_at` is skipped on purpose: every payload carries a fresh one, so
  comparing it would make every row look changed and the count meaningless.
*/
const IGNORED = new Set(['updated_at', 'created_at']);

export function changedFields(before, payload) {
  return Object.keys(payload || {}).filter(k => {
    if (IGNORED.has(k)) return false;
    return !same(before?.[k], payload[k]);
  });
}

/*
  **An empty box and nothing at all are the same thing here**, and a number
  typed as text is the same number. A file that came out of a spreadsheet will
  differ from the database in exactly those two ways on almost every row, and
  counting those as changes would make the preview useless.
*/
function same(a, b) {
  if (a === b) return true;
  if (blank(a) && blank(b)) return true;
  if (typeof a === 'number' || typeof b === 'number') {
    const x = Number(a), y = Number(b);
    if (!Number.isNaN(x) && !Number.isNaN(y)) return x === y;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') return !!a === !!b;
  return String(a ?? '').trim() === String(b ?? '').trim();
}

function blank(v) {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
}

/*
  The sentence above the counts. It says what the file will do in the order a
  person cares about, and **says the word "nothing" out loud** when there is
  nothing to do, rather than showing three zeroes and an enabled button.
*/
export function planSentence(plan, noun = 'row') {
  const bits = [];
  if (plan.create.length) bits.push(plan.create.length.toLocaleString() + ' new');
  if (plan.update.length) bits.push(plan.update.length.toLocaleString() + ' changed');
  if (!bits.length) return 'This file would change nothing.';
  const plural = plan.create.length + plan.update.length === 1 ? noun : noun + 's';
  return 'This file would write ' + bits.join(' and ') + ' ' + plural + '.';
}

export function planIsEmpty(plan) {
  return plan.create.length === 0 && plan.update.length === 0;
}

/*
  A changed field, in words rather than in the name of a database column.

  **`warehouse_manager` and `is_inactive` are not English** and the owner of this
  app does not read code. The preview lists what a row would change, so those
  names would have been the first thing on it.
*/
export function fieldWords(key) {
  const bare = String(key || '').replace(/^is_/, '').replace(/_/g, ' ').trim();
  if (!bare) return '';
  return bare.charAt(0).toUpperCase() + bare.slice(1);
}
